import { z } from 'zod';
import { timesheetsBillingCodeSchema, timesheetsEntryKindSchema, timesheetsEntryStateSchema, timesheetsTimeEntryIdSchema, } from './timesheets-clock.contract.js';
import { timesheetsReviewStateSchema } from './timesheets-entry.contract.js';
/**
 * Public Timesheets labor-cost boundary. The original CRM mixed payroll math, mileage, Finance
 * reimbursements, and response formatting inside one route. This contract carries only Timesheets-
 * owned, snapshotted labor facts in exact integer units so Finance and future export hosts cannot
 * reach Timesheets tables or silently recompute historical policy.
 */
export const timesheetsPayrollCostSummaryCapability = 'timesheets.payroll-cost-summary.v1';
export const timesheetsPayrollMoneyCentsSchema = z.number().int().min(0).max(1_000_000_000_000_000);
export const timesheetsPayrollSecondsSchema = z.number().int().min(0).max(1_000_000_000_000_000);
export const timesheetsPayrollBasisPointsSchema = z.number().int().min(0).max(100_000);
export const timesheetsPayrollCurrencyCodeSchema = z.string().regex(/^[A-Z]{3}$/);
export const timesheetsPayrollIsoWeekdaySchema = z.number().int().min(1).max(7);
export const timesheetsPayrollTimeZoneSchema = z.string().trim().min(1).max(100).refine((value) => {
    try {
        new Intl.DateTimeFormat('en-US', { timeZone: value }).format(new Date(0));
        return true;
    }
    catch {
        return false;
    }
}, 'Timesheets payroll time zone must be an IANA time zone.');
export const timesheetsPayrollRangeSchema = z.strictObject({
    fromInclusive: z.string().datetime({ offset: true }),
    toExclusive: z.string().datetime({ offset: true }),
}).refine((range) => new Date(range.toExclusive).getTime() > new Date(range.fromInclusive).getTime(), { path: ['toExclusive'], message: 'Timesheets payroll range must have a positive half-open interval.' });
export const timesheetsPayrollDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/).refine((value) => {
    const parsed = new Date(`${value}T00:00:00.000Z`);
    return !Number.isNaN(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value;
}, 'Timesheets payroll dates must be real ISO calendar dates.');
export const timesheetsPayrollPayStateSchema = z.enum(['payable', 'pending', 'excluded']);
export const timesheetsPayrollWarningSchema = z.enum([
    'active-entry',
    'entry-needs-correction',
    'rejected-entry',
    'voided-entry',
]);
export function isTimesheetsPayrollExcludedEntry(entry) {
    return entry.state !== 'completed' || ['rejected', 'voided'].includes(entry.reviewState);
}
export const timesheetsEntryPaySnapshotSchema = z.strictObject({
    snapshotId: z.string().uuid(),
    policyVersionId: z.string().uuid(),
    payRateVersionId: z.string().uuid().nullable(),
    hourlyRateCents: timesheetsPayrollMoneyCentsSchema,
    payMultiplierBasisPoints: timesheetsPayrollBasisPointsSchema,
    overtimeMultiplierBasisPoints: timesheetsPayrollBasisPointsSchema,
    weeklyOvertimeEligible: z.boolean(),
    calloutExclusionSeconds: timesheetsPayrollSecondsSchema,
    calloutBonusCents: timesheetsPayrollMoneyCentsSchema,
});
export const timesheetsPayrollWorkReferenceSchema = z.strictObject({
    id: z.string().uuid(),
    label: z.string().trim().min(1).max(240),
});
const timesheetsPayrollCostInputEntryFields = {
    tenantId: z.string().uuid(),
    entryId: timesheetsTimeEntryIdSchema,
    membershipId: z.string().uuid(),
    state: timesheetsEntryStateSchema,
    reviewState: timesheetsReviewStateSchema,
    entryKind: timesheetsEntryKindSchema,
    billingCode: timesheetsBillingCodeSchema,
    workReference: timesheetsPayrollWorkReferenceSchema.nullable(),
    clockInOccurredAt: z.string().datetime({ offset: true }),
    durationSeconds: timesheetsPayrollSecondsSchema.max(86_400),
    paySnapshot: timesheetsEntryPaySnapshotSchema.nullable(),
};
/** Internal read model used to diagnose missing/stale basis before money calculation. */
export const timesheetsPayrollProjectedCostInputEntrySchema = z.strictObject(timesheetsPayrollCostInputEntryFields);
export const timesheetsPayrollCostInputEntrySchema = z.strictObject(timesheetsPayrollCostInputEntryFields).superRefine((entry, context) => {
    const snapshot = entry.paySnapshot;
    const excluded = isTimesheetsPayrollExcludedEntry(entry);
    if (snapshot === null && !excluded) {
        context.addIssue({
            code: 'custom',
            path: ['paySnapshot'],
            message: 'Payable and pending Timesheets entries require a current immutable pay snapshot.',
        });
    }
    if (snapshot !== null && snapshot.calloutExclusionSeconds > entry.durationSeconds) {
        context.addIssue({
            code: 'custom',
            path: ['paySnapshot', 'calloutExclusionSeconds'],
            message: 'Timesheets callout exclusion cannot exceed the entry duration.',
        });
    }
    if (snapshot !== null &&
        snapshot.calloutExclusionSeconds === 0 &&
        snapshot.calloutBonusCents !== 0) {
        context.addIssue({
            code: 'custom',
            path: ['paySnapshot', 'calloutBonusCents'],
            message: 'Timesheets callout bonus requires excluded callout time.',
        });
    }
    if (snapshot !== null && snapshot.calloutExclusionSeconds > 0 && entry.billingCode !== 'after_hours') {
        context.addIssue({
            code: 'custom',
            path: ['paySnapshot', 'calloutExclusionSeconds'],
            message: 'Timesheets callout exclusion is only valid for after-hours work.',
        });
    }
    if (snapshot !== null && entry.billingCode === 'after_hours' && snapshot.weeklyOvertimeEligible) {
        context.addIssue({
            code: 'custom',
            path: ['paySnapshot', 'weeklyOvertimeEligible'],
            message: 'Timesheets after-hours premium cannot stack with weekly overtime.',
        });
    }
    if (entry.entryKind === 'break') {
        if (entry.billingCode !== 'break') {
            context.addIssue({ code: 'custom', path: ['billingCode'], message: 'Break entries use break billing.' });
        }
        if (entry.workReference !== null) {
            context.addIssue({ code: 'custom', path: ['workReference'], message: 'Break entries cannot reference a Project.' });
        }
        if (snapshot !== null && (snapshot.payRateVersionId !== null ||
            snapshot.hourlyRateCents !== 0 ||
            snapshot.payMultiplierBasisPoints !== 0 ||
            snapshot.overtimeMultiplierBasisPoints !== 0 ||
            snapshot.weeklyOvertimeEligible ||
            snapshot.calloutExclusionSeconds !== 0 ||
            snapshot.calloutBonusCents !== 0)) {
            context.addIssue({
                code: 'custom',
                path: ['paySnapshot'],
                message: 'Break snapshots must carry zero pay and no overtime eligibility.',
            });
        }
    }
    else if (entry.billingCode === 'break') {
        context.addIssue({ code: 'custom', path: ['billingCode'], message: 'Work entries cannot use break billing.' });
    }
    else {
        if (snapshot !== null && snapshot.payRateVersionId === null) {
            context.addIssue({
                code: 'custom',
                path: ['paySnapshot', 'payRateVersionId'],
                message: 'Timesheets work snapshots require a pay-rate version.',
            });
        }
        if (snapshot !== null && snapshot.payMultiplierBasisPoints === 0) {
            context.addIssue({
                code: 'custom',
                path: ['paySnapshot', 'payMultiplierBasisPoints'],
                message: 'Timesheets work snapshots require a positive pay multiplier.',
            });
        }
        if (snapshot !== null &&
            snapshot.weeklyOvertimeEligible &&
            snapshot.overtimeMultiplierBasisPoints === 0) {
            context.addIssue({
                code: 'custom',
                path: ['paySnapshot', 'overtimeMultiplierBasisPoints'],
                message: 'Timesheets overtime-eligible work requires a positive overtime multiplier.',
            });
        }
        if (entry.state === 'completed' &&
            ['approved', 'pending'].includes(entry.reviewState) &&
            entry.durationSeconds === 0) {
            context.addIssue({
                code: 'custom',
                path: ['durationSeconds'],
                message: 'Timesheets payable or pending work must have a positive duration.',
            });
        }
    }
    if (entry.state !== 'completed' &&
        ['approved', 'rejected'].includes(entry.reviewState)) {
        context.addIssue({
            code: 'custom',
            path: ['reviewState'],
            message: 'Only completed entries can be approved or rejected.',
        });
    }
});
export const timesheetsPayrollWeeklyCarryInSchema = z.strictObject({
    membershipId: z.string().uuid(),
    weekStartDate: timesheetsPayrollDateSchema,
    hourlyEligibleSecondsBeforeRange: timesheetsPayrollSecondsSchema,
});
export const timesheetsPayrollCostSummaryInputSchema = z.strictObject({
    tenantId: z.string().uuid(),
    currencyCode: timesheetsPayrollCurrencyCodeSchema,
    timeZone: timesheetsPayrollTimeZoneSchema,
    weekStartsOnIsoDay: timesheetsPayrollIsoWeekdaySchema.default(1),
    overtimeThresholdSeconds: timesheetsPayrollSecondsSchema.min(1).max(604_800),
    policyVersionIds: z.array(z.string().uuid()).max(1_000),
    range: timesheetsPayrollRangeSchema,
    weeklyCarryIns: z.array(timesheetsPayrollWeeklyCarryInSchema).max(10_000).default([]),
    entries: z.array(timesheetsPayrollCostInputEntrySchema).max(10_000),
}).superRefine((input, context) => {
    const from = new Date(input.range.fromInclusive).getTime();
    const to = new Date(input.range.toExclusive).getTime();
    const seenEntryIds = new Set();
    const seenCarryIns = new Set();
    input.weeklyCarryIns.forEach((carryIn, index) => {
        const key = `${carryIn.membershipId}|${carryIn.weekStartDate}`;
        if (seenCarryIns.has(key)) {
            context.addIssue({
                code: 'custom',
                path: ['weeklyCarryIns', index],
                message: 'Timesheets payroll input contains a duplicate weekly carry-in.',
            });
        }
        seenCarryIns.add(key);
    });
    const declaredPolicyVersions = new Set(input.policyVersionIds);
    if (declaredPolicyVersions.size !== input.policyVersionIds.length) {
        context.addIssue({
            code: 'custom',
            path: ['policyVersionIds'],
            message: 'Timesheets payroll input contains a duplicate policy version.',
        });
    }
    input.entries.forEach((entry, index) => {
        if (entry.tenantId !== input.tenantId) {
            context.addIssue({
                code: 'custom',
                path: ['entries', index, 'tenantId'],
                message: 'Timesheets payroll input cannot cross tenant boundaries.',
            });
        }
        if (seenEntryIds.has(entry.entryId)) {
            context.addIssue({
                code: 'custom',
                path: ['entries', index, 'entryId'],
                message: 'Timesheets payroll input contains a duplicate entry.',
            });
        }
        seenEntryIds.add(entry.entryId);
        if (entry.paySnapshot !== null && !declaredPolicyVersions.has(entry.paySnapshot.policyVersionId)) {
            context.addIssue({
                code: 'custom',
                path: ['entries', index, 'paySnapshot', 'policyVersionId'],
                message: 'Timesheets payroll snapshot policy is absent from the summary input.',
            });
        }
        const clockIn = new Date(entry.clockInOccurredAt).getTime();
        if (clockIn < from || clockIn >= to) {
            context.addIssue({
                code: 'custom',
                path: ['entries', index, 'clockInOccurredAt'],
                message: 'Timesheets payroll entry falls outside the requested half-open range.',
            });
        }
    });
});
export const timesheetsPayrollMoneyBreakdownSchema = z.strictObject({
    regularPayCents: timesheetsPayrollMoneyCentsSchema,
    overtimePayCents: timesheetsPayrollMoneyCentsSchema,
    hourlyPayCents: timesheetsPayrollMoneyCentsSchema,
    calloutBonusCents: timesheetsPayrollMoneyCentsSchema,
    laborCents: timesheetsPayrollMoneyCentsSchema,
}).superRefine((money, context) => {
    if (money.hourlyPayCents !== money.regularPayCents + money.overtimePayCents) {
        context.addIssue({ code: 'custom', path: ['hourlyPayCents'], message: 'Hourly pay does not reconcile.' });
    }
    if (money.laborCents !== money.hourlyPayCents + money.calloutBonusCents) {
        context.addIssue({ code: 'custom', path: ['laborCents'], message: 'Labor pay does not reconcile.' });
    }
});
const timesheetsPayrollAggregateFields = {
    entryCount: z.number().int().nonnegative(),
    payableEntryCount: z.number().int().nonnegative(),
    pendingEntryCount: z.number().int().nonnegative(),
    excludedEntryCount: z.number().int().nonnegative(),
    trackedWorkSeconds: timesheetsPayrollSecondsSchema,
    breakSeconds: timesheetsPayrollSecondsSchema,
    hourlyEligibleSeconds: timesheetsPayrollSecondsSchema,
    regularSeconds: timesheetsPayrollSecondsSchema,
    overtimeSeconds: timesheetsPayrollSecondsSchema,
    payable: timesheetsPayrollMoneyBreakdownSchema,
    pending: timesheetsPayrollMoneyBreakdownSchema,
};
function refineAggregate(aggregate, context) {
    if (aggregate.entryCount !==
        aggregate.payableEntryCount + aggregate.pendingEntryCount + aggregate.excludedEntryCount) {
        context.addIssue({ code: 'custom', path: ['entryCount'], message: 'Payroll entry counts do not reconcile.' });
    }
    if (aggregate.hourlyEligibleSeconds !== aggregate.regularSeconds + aggregate.overtimeSeconds) {
        context.addIssue({
            code: 'custom',
            path: ['hourlyEligibleSeconds'],
            message: 'Payroll eligible seconds do not reconcile.',
        });
    }
}
export const timesheetsPayrollAggregateSchema = z.strictObject(timesheetsPayrollAggregateFields)
    .superRefine(refineAggregate);
export const timesheetsPayrollCostEntrySchema = z.strictObject({
    entryId: timesheetsTimeEntryIdSchema,
    membershipId: z.string().uuid(),
    workReference: timesheetsPayrollWorkReferenceSchema.nullable(),
    weekStartDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    payState: timesheetsPayrollPayStateSchema,
    reviewState: timesheetsReviewStateSchema,
    warning: timesheetsPayrollWarningSchema.nullable(),
    trackedWorkSeconds: timesheetsPayrollSecondsSchema,
    breakSeconds: timesheetsPayrollSecondsSchema,
    hourlyEligibleSeconds: timesheetsPayrollSecondsSchema,
    regularSeconds: timesheetsPayrollSecondsSchema,
    overtimeSeconds: timesheetsPayrollSecondsSchema,
    hourlyRateCents: timesheetsPayrollMoneyCentsSchema.nullable(),
    payMultiplierBasisPoints: timesheetsPayrollBasisPointsSchema.nullable(),
    overtimeMultiplierBasisPoints: timesheetsPayrollBasisPointsSchema.nullable(),
    regularPayCents: timesheetsPayrollMoneyCentsSchema,
    overtimePayCents: timesheetsPayrollMoneyCentsSchema,
    hourlyPayCents: timesheetsPayrollMoneyCentsSchema,
    calloutBonusCents: timesheetsPayrollMoneyCentsSchema,
    calculatedLaborCents: timesheetsPayrollMoneyCentsSchema,
    payableLaborCents: timesheetsPayrollMoneyCentsSchema,
    pendingLaborCents: timesheetsPayrollMoneyCentsSchema,
    snapshotId: z.string().uuid().nullable(),
    policyVersionId: z.string().uuid().nullable(),
    payRateVersionId: z.string().uuid().nullable(),
}).superRefine((entry, context) => {
    if (entry.hourlyEligibleSeconds !== entry.regularSeconds + entry.overtimeSeconds) {
        context.addIssue({ code: 'custom', path: ['hourlyEligibleSeconds'], message: 'Entry seconds do not reconcile.' });
    }
    if (entry.hourlyPayCents !== entry.regularPayCents + entry.overtimePayCents) {
        context.addIssue({ code: 'custom', path: ['hourlyPayCents'], message: 'Entry hourly pay does not reconcile.' });
    }
    if (entry.calculatedLaborCents !== entry.hourlyPayCents + entry.calloutBonusCents) {
        context.addIssue({ code: 'custom', path: ['calculatedLaborCents'], message: 'Entry labor pay does not reconcile.' });
    }
    const expectedPayable = entry.payState === 'payable' ? entry.calculatedLaborCents : 0;
    const expectedPending = entry.payState === 'pending' ? entry.calculatedLaborCents : 0;
    if (entry.payableLaborCents !== expectedPayable) {
        context.addIssue({ code: 'custom', path: ['payableLaborCents'], message: 'Entry payable pay is inconsistent.' });
    }
    if (entry.pendingLaborCents !== expectedPending) {
        context.addIssue({ code: 'custom', path: ['pendingLaborCents'], message: 'Entry pending pay is inconsistent.' });
    }
    const hasCompleteBasis = entry.snapshotId !== null && entry.policyVersionId !== null &&
        entry.hourlyRateCents !== null && entry.payMultiplierBasisPoints !== null &&
        entry.overtimeMultiplierBasisPoints !== null;
    if (entry.payState !== 'excluded' && !hasCompleteBasis) {
        context.addIssue({ code: 'custom', path: ['snapshotId'], message: 'Payroll-bearing entry lacks a complete immutable basis.' });
    }
    if (entry.snapshotId === null && (entry.policyVersionId !== null || entry.payRateVersionId !== null ||
        entry.hourlyRateCents !== null || entry.payMultiplierBasisPoints !== null ||
        entry.overtimeMultiplierBasisPoints !== null)) {
        context.addIssue({ code: 'custom', path: ['snapshotId'], message: 'Absent payroll snapshot cannot expose partial basis fields.' });
    }
});
export const timesheetsPayrollMembershipSummarySchema = z.strictObject({
    membershipId: z.string().uuid(),
    ...timesheetsPayrollAggregateFields,
}).superRefine(refineAggregate);
export const timesheetsPayrollWorkReferenceSummarySchema = z.strictObject({
    workReference: timesheetsPayrollWorkReferenceSchema.nullable(),
    ...timesheetsPayrollAggregateFields,
}).superRefine(refineAggregate);
export const timesheetsPayrollCostSummarySchema = z.strictObject({
    contractVersion: z.literal(timesheetsPayrollCostSummaryCapability),
    tenantId: z.string().uuid(),
    currencyCode: timesheetsPayrollCurrencyCodeSchema,
    timeZone: timesheetsPayrollTimeZoneSchema,
    weekStartsOnIsoDay: timesheetsPayrollIsoWeekdaySchema,
    overtimeThresholdSeconds: timesheetsPayrollSecondsSchema.min(1).max(604_800),
    range: timesheetsPayrollRangeSchema,
    policyVersionIds: z.array(z.string().uuid()).max(1_000),
    weeklyCarryIns: z.array(timesheetsPayrollWeeklyCarryInSchema).max(10_000),
    entries: z.array(timesheetsPayrollCostEntrySchema),
    totals: timesheetsPayrollAggregateSchema,
    byMembership: z.array(timesheetsPayrollMembershipSummarySchema),
    byWorkReference: z.array(timesheetsPayrollWorkReferenceSummarySchema),
}).superRefine((summary, context) => {
    const expectedAggregate = (entries) => {
        const payableEntries = entries.filter((entry) => entry.payState === 'payable');
        const pendingEntries = entries.filter((entry) => entry.payState === 'pending');
        const money = (selected) => {
            const regularPayCents = selected.reduce((sum, entry) => sum + entry.regularPayCents, 0);
            const overtimePayCents = selected.reduce((sum, entry) => sum + entry.overtimePayCents, 0);
            const calloutBonusCents = selected.reduce((sum, entry) => sum + entry.calloutBonusCents, 0);
            const hourlyPayCents = regularPayCents + overtimePayCents;
            return {
                regularPayCents,
                overtimePayCents,
                hourlyPayCents,
                calloutBonusCents,
                laborCents: hourlyPayCents + calloutBonusCents,
            };
        };
        return {
            entryCount: entries.length,
            payableEntryCount: payableEntries.length,
            pendingEntryCount: pendingEntries.length,
            excludedEntryCount: entries.length - payableEntries.length - pendingEntries.length,
            trackedWorkSeconds: entries.reduce((sum, entry) => sum + entry.trackedWorkSeconds, 0),
            breakSeconds: entries.reduce((sum, entry) => sum + entry.breakSeconds, 0),
            hourlyEligibleSeconds: entries.reduce((sum, entry) => sum + entry.hourlyEligibleSeconds, 0),
            regularSeconds: entries.reduce((sum, entry) => sum + entry.regularSeconds, 0),
            overtimeSeconds: entries.reduce((sum, entry) => sum + entry.overtimeSeconds, 0),
            payable: money(payableEntries),
            pending: money(pendingEntries),
        };
    };
    const assertAggregate = (actual, expectedEntries, path) => {
        if (JSON.stringify(actual) !== JSON.stringify(expectedAggregate(expectedEntries))) {
            context.addIssue({ code: 'custom', path, message: 'Payroll aggregate does not reconcile to its entries.' });
        }
    };
    if (summary.totals.entryCount !== summary.entries.length) {
        context.addIssue({ code: 'custom', path: ['totals', 'entryCount'], message: 'Payroll total count does not match entries.' });
    }
    const membershipCount = summary.byMembership.reduce((sum, row) => sum + row.entryCount, 0);
    const workReferenceCount = summary.byWorkReference.reduce((sum, row) => sum + row.entryCount, 0);
    if (membershipCount !== summary.entries.length) {
        context.addIssue({ code: 'custom', path: ['byMembership'], message: 'Membership rows do not cover every entry.' });
    }
    if (workReferenceCount !== summary.entries.length) {
        context.addIssue({ code: 'custom', path: ['byWorkReference'], message: 'Work-reference rows do not cover every entry.' });
    }
    assertAggregate(summary.totals, summary.entries, ['totals']);
    const seenMembershipIds = new Set();
    summary.byMembership.forEach((row, index) => {
        if (seenMembershipIds.has(row.membershipId)) {
            context.addIssue({ code: 'custom', path: ['byMembership', index, 'membershipId'], message: 'Duplicate membership summary.' });
        }
        seenMembershipIds.add(row.membershipId);
        const { membershipId: _membershipId, ...aggregate } = row;
        assertAggregate(aggregate, summary.entries.filter((entry) => entry.membershipId === row.membershipId), ['byMembership', index]);
    });
    const seenWorkReferences = new Set();
    summary.byWorkReference.forEach((row, index) => {
        const workReferenceKey = row.workReference?.id ?? '__unassigned__';
        if (seenWorkReferences.has(workReferenceKey)) {
            context.addIssue({ code: 'custom', path: ['byWorkReference', index, 'workReference'], message: 'Duplicate work-reference summary.' });
        }
        seenWorkReferences.add(workReferenceKey);
        const { workReference: _workReference, ...aggregate } = row;
        assertAggregate(aggregate, summary.entries.filter((entry) => (entry.workReference?.id ?? '__unassigned__') === workReferenceKey), ['byWorkReference', index]);
    });
    const declaredPolicyVersions = new Set(summary.policyVersionIds);
    if (declaredPolicyVersions.size !== summary.policyVersionIds.length) {
        context.addIssue({ code: 'custom', path: ['policyVersionIds'], message: 'Duplicate payroll policy version.' });
    }
    summary.entries.forEach((entry, index) => {
        if (entry.policyVersionId !== null && !declaredPolicyVersions.has(entry.policyVersionId)) {
            context.addIssue({
                code: 'custom',
                path: ['entries', index, 'policyVersionId'],
                message: 'Entry policy version is absent from the payroll summary.',
            });
        }
    });
    const seenCarryIns = new Set();
    summary.weeklyCarryIns.forEach((carryIn, index) => {
        const key = `${carryIn.membershipId}|${carryIn.weekStartDate}`;
        if (seenCarryIns.has(key)) {
            context.addIssue({
                code: 'custom',
                path: ['weeklyCarryIns', index],
                message: 'Duplicate weekly carry-in in payroll summary.',
            });
        }
        seenCarryIns.add(key);
    });
});
export const timesheetsPayrollCostSummaryQuerySchema = z.strictObject({
    fromInclusive: z.string().datetime({ offset: true }),
    toExclusive: z.string().datetime({ offset: true }),
}).superRefine((range, context) => {
    const from = new Date(range.fromInclusive).getTime();
    const to = new Date(range.toExclusive).getTime();
    if (to <= from) {
        context.addIssue({ code: 'custom', path: ['toExclusive'], message: 'Timesheets payroll range must be positive.' });
    }
    if (to - from > 93 * 24 * 60 * 60 * 1_000) {
        context.addIssue({ code: 'custom', path: ['toExclusive'], message: 'Timesheets payroll range cannot exceed 93 days.' });
    }
});
export const timesheetsPayrollBasisIssueCodeSchema = z.enum([
    'missing-current-pay-snapshot',
    'stale-pay-snapshot',
    'mixed-core-policy-basis',
    'published-payroll-policy-unavailable',
]);
export const timesheetsPayrollBasisIssueSchema = z.strictObject({
    code: timesheetsPayrollBasisIssueCodeSchema,
    entryId: timesheetsTimeEntryIdSchema.nullable(),
    membershipId: z.string().uuid().nullable(),
    clockInOccurredAt: z.string().datetime({ offset: true }).nullable(),
    affects: z.enum(['range-entry', 'weekly-carry-in', 'summary']),
});
export const timesheetsPayrollCostSummaryReadyResponseSchema = z.strictObject({
    status: z.literal('ready'),
    data: timesheetsPayrollCostSummarySchema,
    issues: z.tuple([]),
});
export const timesheetsPayrollCostSummaryBasisIncompleteResponseSchema = z.strictObject({
    status: z.literal('basis-incomplete'),
    data: z.null(),
    range: timesheetsPayrollRangeSchema,
    issues: z.array(timesheetsPayrollBasisIssueSchema).min(1).max(10_000),
});
export const timesheetsPayrollCostSummaryResponseSchema = z.discriminatedUnion('status', [
    timesheetsPayrollCostSummaryReadyResponseSchema,
    timesheetsPayrollCostSummaryBasisIncompleteResponseSchema,
]);
//# sourceMappingURL=timesheets-payroll-cost-summary.contract.js.map